// The checker's type model — the core data the checker, the standard library,
// and the editor language service all speak in.
//
// This is a leaf module: it imports nothing, so both `checker.ts` and
// `stdlib.ts` can depend on it without an import cycle. The declarative built-in
// tables in `stdlib.ts` are built from the primitive `Type` constants and the
// container-signature types defined here.

export type Type =
  | { k: "int" }
  | { k: "float" }
  | { k: "bool" }
  | { k: "string" }
  // A single Unicode code point (a `uint32`-ish scalar). `str.charAt` yields one;
  // `'x'` is a char literal. Compared/ordered by code point, hashable, printable.
  | { k: "char" }
  | { k: "void" }
  | { k: "unknown" }
  | { k: "struct"; name: string }
  // A generic struct instantiation, e.g. `Pair<int, string>`. `name` is the
  // struct name, `args` its resolved type arguments (same arity as the struct's
  // typeParams). Mirrors `enum-generic`.
  | { k: "struct-generic"; name: string; args: Type[] }
  | { k: "enum"; name: string }
  // A generic enum instantiation, e.g. `Option<int>`. `name` is the enum name,
  // `args` its resolved type arguments (same arity as the enum's typeParams).
  | { k: "enum-generic"; name: string; args: Type[] }
  // A trait type, e.g. `Iterator<int>`. A value of this type is any concrete
  // struct/enum that implements that exact trait instantiation; method calls on
  // it dispatch dynamically. Its members are the trait's method signatures with
  // the trait arguments substituted.
  | { k: "trait"; name: string; args: Type[] }
  // A generic function's type parameter (`T` in `fn f<T: Ord>(...)`), in scope
  // while that function is checked. `bounds` are the trait names it must satisfy;
  // its members are those traits' methods. At a call site `T` is inferred from
  // the arguments and substituted away, so it never escapes into a stored value.
  | { k: "var"; name: string; bounds: string[] }
  // Any built-in generic container: `name` is the container ("Set"/"Dict"/...),
  // `args` its type arguments. One representation for all containers means the
  // checker treats them uniformly — like a generic struct, but tagged `generic` so
  // the backends recognize a natively-backed collection.
  | { k: "generic"; name: string; args: Type[] }
  // A first-class function type, e.g. `(int) -> bool`. A value of this type is a
  // reference to a (non-generic) top-level function; it can be passed as an
  // argument, stored, and called. `params` are the parameter types, `ret` the
  // result type (`void` when it returns nothing).
  | { k: "fn"; params: Type[]; ret: Type };

export const INT: Type = { k: "int" };
export const FLOAT: Type = { k: "float" };
export const BOOL: Type = { k: "bool" };
export const STRING: Type = { k: "string" };
export const CHAR: Type = { k: "char" };
export const VOID: Type = { k: "void" };
export const UNKNOWN: Type = { k: "unknown" };
